import { useState, useEffect } from 'react'
import { Routes, Route, useNavigate } from 'react-router-dom'
import { fetchUsers } from '../../services/admin'
import DashboardShell from '../ui/DashboardShell'
import AdminHome from './AdminHome'
import UsersPanel from './UsersPanel'
import InvitesPanel from './InvitesPanel'
import UsagePanel from './UsagePanel'
import EmulatePanel from './EmulatePanel'
import DocsPanel from './DocsPanel'
import BugReportsPanel from './BugReportsPanel'

export default function AdminDashboard() {
  const navigate = useNavigate()
  const [authorized, setAuthorized] = useState<boolean | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchUsers()
      .then(() => setAuthorized(true))
      .catch((err) => {
        if (err.message === 'forbidden') {
          setAuthorized(false)
        } else {
          setError('Failed to reach server. Please try again.')
        }
      })
  }, [])

  if (authorized === null && !error) {
    return (
      <div
        className="flex items-center justify-center"
        style={{ minHeight: '100vh' }}
      >
        <p className="font-mono text-sm text-slate-500 animate-pulse">Checking access...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-4"
        style={{ minHeight: '100vh' }}
      >
        <p className="font-mono text-sm text-red-400">{error}</p>
        <button
          onClick={() => { setError(null); window.location.reload() }}
          className="font-mono text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
        >
          Retry
        </button>
      </div>
    )
  }

  if (!authorized) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-4"
        style={{ minHeight: '100vh' }}
      >
        <p className="font-mono text-sm text-red-400">Not authorized</p>
        <button
          onClick={() => navigate('/dashboard')}
          className="font-mono text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
        >
          Back to Dashboard
        </button>
      </div>
    )
  }

  return (
    <Routes>
      <Route
        index
        element={
          <DashboardShell
            title="ADMIN"
            subtitle="Manage users, invites, and monitor usage."
          >
            <AdminHome />
          </DashboardShell>
        }
      />
      <Route
        path="users"
        element={
          <DashboardShell
            title="USERS"
            subtitle="Manage registered users."
            backTo={{ label: 'Admin', path: '/admin' }}
          >
            <UsersPanel />
          </DashboardShell>
        }
      />
      <Route
        path="invites"
        element={
          <DashboardShell
            title="INVITES"
            subtitle="Invite codes and access control."
            backTo={{ label: 'Admin', path: '/admin' }}
          >
            <InvitesPanel />
          </DashboardShell>
        }
      />
      <Route
        path="usage"
        element={
          <DashboardShell
            title="USAGE"
            subtitle="Platform usage and quotas."
            backTo={{ label: 'Admin', path: '/admin' }}
          >
            <UsagePanel />
          </DashboardShell>
        }
      />
      <Route
        path="emulate"
        element={
          <DashboardShell
            title="EMULATE"
            subtitle="Emulate user sessions."
            backTo={{ label: 'Admin', path: '/admin' }}
          >
            <EmulatePanel />
          </DashboardShell>
        }
      />
      <Route
        path="docs"
        element={
          <DashboardShell
            title="DOCS"
            subtitle="Manage public documentation articles."
            backTo={{ label: 'Admin', path: '/admin' }}
          >
            <DocsPanel />
          </DashboardShell>
        }
      />
      <Route
        path="bugs"
        element={
          <DashboardShell
            title="BUG REPORTS"
            subtitle="User-submitted bug reports."
            backTo={{ label: 'Admin', path: '/admin' }}
          >
            <BugReportsPanel />
          </DashboardShell>
        }
      />
    </Routes>
  )
}
